import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  EyeSlashIcon,
  FlagIcon,
  HashtagIcon,
  MapPinIcon,
  PencilSquareIcon,
  TrashIcon,
  UserMinusIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { useFetcher } from '@remix-run/react';
import { useQueryClient } from '@tanstack/react-query';

import { COMMUNITY_ROUTES } from '~/constants/community';
import { useEnsureCsrfToken } from '~/context/CsrfContext';
import useToast, { type ToastData, ToastStatus } from '~/hooks/useToast';
import type { CommunityPost } from '~/interfaces/community';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import type { ActionMenuItem } from './ui/ActionMenu';
import { ActionMenu } from './ui/ActionMenu';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { ReportReasonFields } from './ui/ReportReasonFields';

import { useCommunityMemberActions } from '../_context/CommunityMemberActionsProvider';
import { useCommunityPostActions } from '../_context/CommunityPostActionsProvider';
import { useBlockMemberMutation } from '../_hooks/useBlockMemberMutation';
import { communityPostsKeys } from '../_hooks/useCommunityPosts';
import { useMemberBlockingEnabled } from '../_hooks/useMemberBlockingEnabled';
import { useMemberModeration } from '../_hooks/useMemberModeration';
import { useNavigateToPost } from '../_hooks/useNavigateToPost';
import { PIN_FETCHER_KEY, PIN_POST_ACTION } from '../_hooks/usePinPostFetcher';
import type { CommunityPostPermissions } from '../_hooks/usePostPermissions';
import { usePostPermissions } from '../_hooks/usePostPermissions';
import { PROFILE_PIN_FETCHER_KEY, PROFILE_PIN_POST_ACTION } from '../_hooks/useProfilePinFetcher';
import useReportPostMutation from '../_hooks/useReportPostMutation';
import { useReportToBeehiiv } from '../_hooks/useReportToBeehiiv';
import { SET_COMMENTS_STATE_ACTION, useSetCommentsStateFetcher } from '../_hooks/useSetCommentsStateFetcher';
import { useSpace } from '../_hooks/useSpace';
import { usePostComposer } from './PostComposer';

interface PostActionMenuProps {
  post: CommunityPost;
  triggerVariant?: 'default' | 'glass';
  className?: string;
  backTo?: string;
  alignmentOffset?: number;
}

interface MenuItem {
  key: string;
  labelKey: string;
  icon: FC<{ className?: string }>;
  requiredPerm: keyof CommunityPostPermissions;
  destructive?: boolean;
  onSelect: () => void;
}

export const PostActionMenu: FC<PostActionMenuProps> = ({
  post,
  triggerVariant,
  className,
  backTo,
  alignmentOffset = 0,
}) => {
  const { t } = useTranslation();
  const ensureCsrfToken = useEnsureCsrfToken();
  const navigateToPost = useNavigateToPost();
  const pinFetcher = useFetcher({ key: PIN_FETCHER_KEY });
  const profilePinFetcher = useFetcher({ key: PROFILE_PIN_FETCHER_KEY });
  const memberActions = useCommunityMemberActions();
  const postActions = useCommunityPostActions();
  const reportMutation = useReportPostMutation();
  const { mutate: blockMember } = useBlockMemberMutation();
  const memberBlockingEnabled = useMemberBlockingEnabled();
  const queryClient = useQueryClient();
  const { fetcher: commentsStateFetcher, isSubmitting: isCommentsStateSubmitting } = useSetCommentsStateFetcher(
    post.id,
    {
      onSubmissionComplete: () => {
        setCommentsStateDialog(null);
        setCommentsStateReason('');
      },
    },
  );
  const { openComposer } = usePostComposer();
  const space = useSpace();
  const isSyncedWithWebsite = post.synced_with_website;
  const permissions = usePostPermissions(post);
  const [actionType, setActionType] = useState('');
  const [actionableTarget, setActionableTarget] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [reportToBeehiiv, setReportToBeehiiv] = useState(false);
  const [blockUser, setBlockUser] = useState(false);
  const escalateToBeehiiv = useReportToBeehiiv();
  const [toastData, setToastData] = useState<ToastData>();
  useToast(toastData);
  const [commentsStateDialog, setCommentsStateDialog] = useState<{ target: 'closed' | 'hidden' } | null>(null);
  const [commentsStateReason, setCommentsStateReason] = useState('');

  const isAuthorBlocked = post.author.computed?.blocking === true;
  const { menuItems: moderationMenuItems } = useMemberModeration({
    memberId: post.author.id,
    memberName: post.author.name ?? '',
    canBanAuthor: permissions.canBanAuthor,
    canUnbanAuthor: permissions.canUnbanAuthor,
    canBlockAuthor: permissions.canFollowAuthor && !isAuthorBlocked,
    canUnblockAuthor: permissions.canFollowAuthor && isAuthorBlocked,
  });

  const isInSpaceFeed = !!space;
  const isFollowed = post.author.computed?.following ?? false;
  const { isAuthor } = permissions;
  const isModeratorRemoval = permissions.canDelete && !isAuthor;
  const deleteLabelKey = isModeratorRemoval ? 'comments.removing.remove' : 'community.action_menu.delete_post';

  const items = (() => {
    const groups: MenuItem[][] = [
      // Group 1 — member actions
      [
        ...(!backTo
          ? [
              {
                key: 'go_to_post',
                labelKey: 'community.action_menu.go_to_post',
                icon: ArrowTopRightOnSquareIcon,
                requiredPerm: 'canGoToPost' as keyof CommunityPostPermissions,
                onSelect: () => navigateToPost(post.id),
              },
            ]
          : []),
        {
          key: 'follow-unfollow',
          labelKey: isFollowed ? 'community.action_menu.unfollow_author' : 'community.action_menu.follow_author',
          icon: isFollowed ? UserMinusIcon : UserPlusIcon,
          requiredPerm: 'canFollowAuthor',
          onSelect: () =>
            memberActions.toggleFollow({ id: post.author.id, name: post.author.name ?? '' }, isFollowed, {
              pageBlock: NectarPageBlock.CommunityPostFollowAuthorButton,
            }),
        },
        {
          key: 'report',
          labelKey: 'community.action_menu.report_post',
          icon: FlagIcon,
          requiredPerm: 'canReport',
          onSelect: () => {
            setActionableTarget(post.id);
            setActionType('report');
          },
        },
      ],
      // Group 2 — author / moderation
      [
        ...(isInSpaceFeed && !post.is_pinned
          ? [
              {
                key: 'pin',
                labelKey: 'community.action_menu.pin_post',
                icon: MapPinIcon,
                requiredPerm: 'canPin' as const,
                onSelect: async () => {
                  pinFetcher.submit(
                    { post_id: post.id, action_type: 'pin', csrf: await ensureCsrfToken() },
                    { method: 'POST', action: PIN_POST_ACTION },
                  );
                },
              },
            ]
          : []),
        ...(isInSpaceFeed && post.is_pinned
          ? [
              {
                key: 'unpin',
                labelKey: 'community.action_menu.unpin_post',
                icon: MapPinIcon,
                requiredPerm: 'canPin' as const,
                onSelect: async () => {
                  pinFetcher.submit(
                    { post_id: post.id, action_type: 'unpin', csrf: await ensureCsrfToken() },
                    { method: 'POST', action: PIN_POST_ACTION },
                  );
                },
              },
            ]
          : []),
        {
          key: post.is_profile_pinned ? 'unpin_from_profile' : 'pin_to_profile',
          labelKey: post.is_profile_pinned
            ? 'community.action_menu.unpin_from_profile'
            : 'community.action_menu.pin_to_profile',
          icon: MapPinIcon,
          requiredPerm: 'canPinToProfile',
          onSelect: async () => {
            profilePinFetcher.submit(
              {
                post_id: post.id,
                action_type: post.is_profile_pinned ? 'unpin' : 'pin',
                csrf: await ensureCsrfToken(),
              },
              { method: 'POST', action: PROFILE_PIN_POST_ACTION },
            );
          },
        },
        {
          key: 'move',
          labelKey: 'community.action_menu.move_post',
          icon: HashtagIcon,
          requiredPerm: 'canMove',
          onSelect: () =>
            postActions.openMoveDialog({
              postId: post.id,
              currentSpaceSlug: post.space?.slug ?? null,
              isPinned: post.is_pinned,
            }),
        },
        {
          key: 'edit',
          labelKey: 'community.action_menu.edit_post',
          icon: PencilSquareIcon,
          requiredPerm: 'canEdit',
          onSelect: () =>
            openComposer({
              editPost: post,
              ...(post.space?.slug ? { defaultSpaceSlug: post.space.slug } : {}),
            }),
        },
        {
          key: 'delete',
          labelKey: deleteLabelKey,
          icon: TrashIcon,
          requiredPerm: 'canDelete',
          destructive: true,
          onSelect: () =>
            postActions.openDeleteDialog({ postId: post.id, isModeratorRemoval, isSyncedWithWebsite, backTo }),
        },
      ],
      // Group 3 — comment moderation
      [
        {
          key: 'hide_comments',
          labelKey:
            post.comments_state === 'hidden'
              ? 'community.action_menu.unhide_comments'
              : 'community.action_menu.hide_comments',
          icon: EyeSlashIcon,
          requiredPerm: 'canHideComments',
          onSelect: () => handleCommentsStateClick(post.comments_state === 'hidden' ? 'open' : 'hidden'),
        },
        {
          key: 'close_comments',
          labelKey:
            post.comments_state === 'closed'
              ? 'community.action_menu.reopen_comments'
              : 'community.action_menu.close_comments',
          icon: ChatBubbleOvalLeftEllipsisIcon,
          requiredPerm: 'canCloseComments',
          onSelect: () => handleCommentsStateClick(post.comments_state === 'closed' ? 'open' : 'closed'),
        },
      ],
    ];

    const allowedGroups = groups
      .map((group) => group.filter((item) => permissions[item.requiredPerm]))
      .filter((group) => group.length > 0);

    const flat = allowedGroups.flatMap<ActionMenuItem>((group, groupIndex) => {
      const actionItems = group.map<ActionMenuItem>((item) => ({
        key: item.key,
        label: t(item.labelKey),
        icon: item.icon,
        onClick: item.onSelect,
        variant: item.destructive ? 'danger' : 'default',
      }));

      if (groupIndex === 0) return actionItems;
      return [{ type: 'divider' as const, key: `divider-${groupIndex}` }, ...actionItems];
    });

    if (moderationMenuItems.length > 0) {
      const deleteIndex = flat.findIndex((item) => item.key === 'delete');
      if (deleteIndex !== -1) {
        flat.splice(deleteIndex, 0, ...moderationMenuItems);
      } else {
        flat.push(...moderationMenuItems);
      }
    }

    return flat;
  })();

  const handleCommentsStateClick = async (target: 'open' | 'closed' | 'hidden') => {
    if (target === 'open') {
      commentsStateFetcher.submit(
        { post_id: post.id, state: target, csrf: await ensureCsrfToken() },
        { method: 'PATCH', action: SET_COMMENTS_STATE_ACTION },
      );
      return;
    }
    setCommentsStateDialog({ target });
  };

  const handleConfirmCommentsState = async () => {
    if (!commentsStateDialog) return;
    const reason = commentsStateReason.trim();
    commentsStateFetcher.submit(
      {
        post_id: post.id,
        state: commentsStateDialog.target,
        csrf: await ensureCsrfToken(),
        ...(reason.length > 0 ? { reason } : {}),
      },
      { method: 'PATCH', action: SET_COMMENTS_STATE_ACTION },
    );
  };

  const handleCancelCommentsState = () => {
    setCommentsStateDialog(null);
    setCommentsStateReason('');
  };

  const handleConfirmReport = () => {
    if (!actionableTarget) return;
    const trimmedReason = reportReason.trim();
    reportMutation.mutate(
      { post_id: actionableTarget, reason: trimmedReason },
      {
        onSuccess: (data) => {
          setToastData(data.toast);
          if (blockUser) blockMember({ memberId: post.author.id, block: true });
          setActionType('');
          setActionableTarget(null);
          setReportReason('');
          setReportToBeehiiv(false);
          setBlockUser(false);
          queryClient.invalidateQueries({ queryKey: communityPostsKeys.all });
        },
        onError: () => {
          setToastData({ status: ToastStatus.ERROR, message: t('community.action_menu.report_post_error') });
        },
      },
    );
  };

  const handleCancelReport = () => {
    setActionType('');
    setActionableTarget(null);
    setReportReason('');
    setReportToBeehiiv(false);
    setBlockUser(false);
  };

  return (
    <>
      <ActionMenu
        items={items}
        triggerVariant={triggerVariant}
        className={className}
        alignmentOffset={alignmentOffset}
      />
      <ConfirmDialog
        isOpen={commentsStateDialog !== null}
        title={
          commentsStateDialog?.target === 'hidden'
            ? t('community.action_menu.hide_comments_confirm_title')
            : t('community.action_menu.close_comments_confirm_title')
        }
        description={
          commentsStateDialog?.target === 'hidden'
            ? t('community.action_menu.hide_comments_confirm_description')
            : t('community.action_menu.close_comments_confirm_description')
        }
        confirmLabel={
          commentsStateDialog?.target === 'hidden'
            ? t('community.action_menu.hide_comments_confirm_button')
            : t('community.action_menu.close_comments_confirm_button')
        }
        onConfirm={handleConfirmCommentsState}
        onCancel={handleCancelCommentsState}
        isLoading={isCommentsStateSubmitting}
        body={
          <div className="mt-4">
            <label htmlFor={`comments-state-reason-${post.id}`} className="block text-sm font-medium text-black">
              {t('community.action_menu.comments_state_reason_label')}
            </label>
            <textarea
              id={`comments-state-reason-${post.id}`}
              rows={3}
              value={commentsStateReason}
              onChange={(event) => setCommentsStateReason(event.target.value)}
              placeholder={t('community.action_menu.comments_state_reason_placeholder')}
              className="mt-2 w-full rounded-2xl border border-black/10 px-3 py-2 text-sm text-black placeholder:text-black/40 focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>
        }
      />
      <ConfirmDialog
        isOpen={actionType === 'report'}
        title={t('community.action_menu.report_post_confirm_title')}
        description={t('community.action_menu.report_post_confirm_description')}
        confirmLabel={
          reportToBeehiiv
            ? t('community.report.escalate_link', 'Report to beehiiv')
            : t('community.action_menu.report_post_confirm_button')
        }
        onConfirm={
          reportToBeehiiv
            ? () => {
                escalateToBeehiiv(reportReason, COMMUNITY_ROUTES.post(post.id));
                if (blockUser) blockMember({ memberId: post.author.id, block: true });
                handleCancelReport();
              }
            : handleConfirmReport
        }
        onCancel={handleCancelReport}
        confirmDisabled={reportReason.trim().length === 0}
        isLoading={reportMutation.isPending}
        body={
          <ReportReasonFields
            id={`report-post-reason-${post.id}`}
            label={t('community.action_menu.report_post_reason_label')}
            placeholder={t('community.action_menu.report_post_reason_placeholder')}
            value={reportReason}
            onChange={setReportReason}
            reportToBeehiiv={reportToBeehiiv}
            onReportToBeehiivChange={setReportToBeehiiv}
            showActions={memberBlockingEnabled && !isAuthorBlocked}
            blockUser={blockUser}
            onBlockUserChange={setBlockUser}
          />
        }
      />
    </>
  );
};
